const toNum = (value) => Number(value || 0);
const toInt = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const DEFAULT_LAYERS_PER_DAY = 1;
const WORK_DAYS_PER_WEEK = 5;

export const isForemanJob = (job) => {
  const jobName = String(job?.jobName || job?.job_name || "").toLowerCase();
  return jobName.includes("foreman");
};

export const calculateTeamWeeklyMetrics = (allocations = []) => {
  const teamPainterCount = allocations.reduce(
    (sum, allocation) =>
      sum + toInt(allocation?.memberCount || allocation?.member_count),
    0,
  );

  const teamPaintingHoursPerWeek = allocations.reduce((sum, allocation) => {
    const memberCount = toInt(
      allocation?.memberCount || allocation?.member_count,
    );
    const job = allocation?.job || {};
    const hoursPerWeek = toNum(job?.hoursPerWeek || job?.hours_per_week);
    const paintingPct = toNum(
      job?.paintingTimePct || job?.painting_time_pct || 100,
    );
    return sum + memberCount * hoursPerWeek * (paintingPct / 100);
  }, 0);

  const perTeamForemanPaintingHoursPerWeek = allocations.reduce(
    (sum, allocation) => {
      const memberCount = toInt(
        allocation?.memberCount || allocation?.member_count,
      );
      const job = allocation?.job || {};
      if (!isForemanJob(job)) return sum;
      const hoursPerWeek = toNum(job?.hoursPerWeek || job?.hours_per_week);
      const paintingPct = toNum(
        job?.paintingTimePct || job?.painting_time_pct || 100,
      );
      return sum + memberCount * hoursPerWeek * (paintingPct / 100);
    },
    0,
  );

  const teamWeeklyLaborCost = allocations.reduce((sum, allocation) => {
    const memberCount = toInt(
      allocation?.memberCount || allocation?.member_count,
    );
    const job = allocation?.job || {};
    const rate =
      toNum(job?.convertedRatePerHour || job?.converted_rate_per_hour) ||
      toNum(job?.ratePerHour || job?.rate_per_hour);
    const hoursPerWeek = toNum(job?.hoursPerWeek || job?.hours_per_week);
    return sum + memberCount * rate * hoursPerWeek;
  }, 0);

  const perTeamForemanWeeklyCost = allocations.reduce((sum, allocation) => {
    const memberCount = toInt(
      allocation?.memberCount || allocation?.member_count,
    );
    const job = allocation?.job || {};
    if (!isForemanJob(job)) return sum;
    const rate =
      toNum(job?.convertedRatePerHour || job?.converted_rate_per_hour) ||
      toNum(job?.ratePerHour || job?.rate_per_hour);
    const hoursPerWeek = toNum(job?.hoursPerWeek || job?.hours_per_week);
    return sum + memberCount * rate * hoursPerWeek;
  }, 0);

  return {
    teamPainterCount,
    teamPaintingHoursPerWeek,
    perTeamForemanPaintingHoursPerWeek,
    perTeamNonForemanPaintingHoursPerWeek:
      teamPaintingHoursPerWeek - perTeamForemanPaintingHoursPerWeek,
    teamWeeklyLaborCost,
    perTeamForemanWeeklyCost,
    perTeamNonForemanWeeklyCost: teamWeeklyLaborCost - perTeamForemanWeeklyCost,
  };
};

export const resolveTeamsAndWeeks = ({
  requiredManhours,
  weekLimit,
  foremansLimit,
  perTeamNonForemanPaintingHoursPerWeek,
  perTeamForemanPaintingHoursPerWeek,
  calendarWeeksLowerBound = 0,
}) => {
  const normalizedWeekLimit = Number(weekLimit || 0);
  const normalizedForemansLimit = Math.max(
    0,
    Math.floor(Number(foremansLimit || 0)),
  );
  const normalizedCalendarWeeksLowerBound = Math.max(
    0,
    Number(calendarWeeksLowerBound || 0),
  );

  const getLaborWeeksForTeams = (teamsCount) => {
    const effectivePaintingHoursPerWeek =
      perTeamNonForemanPaintingHoursPerWeek * teamsCount +
      perTeamForemanPaintingHoursPerWeek *
        Math.min(teamsCount, normalizedForemansLimit);

    if (effectivePaintingHoursPerWeek <= 0) return Number.POSITIVE_INFINITY;
    return Number(requiredManhours || 0) / effectivePaintingHoursPerWeek;
  };

  if (
    normalizedWeekLimit > 0 &&
    normalizedCalendarWeeksLowerBound > normalizedWeekLimit
  ) {
    return {
      error: `Weeks Limit is below the minimum calendar duration of ${normalizedCalendarWeeksLowerBound.toFixed(2)} weeks imposed by #Layers per Day. Increase Weeks Limit or raise #Layers per Day.`,
    };
  }

  let teamsRequired = 1;
  let laborWeeksRequired = getLaborWeeksForTeams(teamsRequired);
  let weeksRequired = Math.max(
    laborWeeksRequired,
    normalizedCalendarWeeksLowerBound,
  );
  let guard = 0;

  while (weeksRequired > normalizedWeekLimit && guard < 1000) {
    teamsRequired += 1;
    laborWeeksRequired = getLaborWeeksForTeams(teamsRequired);
    weeksRequired = Math.max(
      laborWeeksRequired,
      normalizedCalendarWeeksLowerBound,
    );
    guard += 1;
  }

  if (!Number.isFinite(weeksRequired)) {
    return {
      error:
        "Team painting capacity is zero after applying # Foremans limit. Increase # Foremans or update team/job setup.",
    };
  }

  return {
    teamsRequired,
    weeksRequired,
    laborWeeksRequired,
    calendarWeeksLowerBound: normalizedCalendarWeeksLowerBound,
    foremansLimit: normalizedForemansLimit,
  };
};

const normalizePositiveNumber = (value, fallback = null) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

const calculateSpanLayerConstraint = (spanResult, layersPerDay) => {
  const normalizedLayersPerDay = normalizePositiveNumber(layersPerDay, null);
  if (!(normalizedLayersPerDay > 0)) {
    return {
      effectiveLayers: 0,
      calendarDaysRequired: 0,
      isConstraining: false,
    };
  }

  const bucketDefinitions = [
    {
      repairArea: toNum(spanResult?.areaA),
      layerUnits: toNum(
        spanResult?.paintLayerUnitsA ??
          spanResult?.paint_layer_units_a ??
          spanResult?.paintLayerCountA ??
          spanResult?.paint_layer_count_a,
      ),
    },
    {
      repairArea: toNum(spanResult?.areaB),
      layerUnits: toNum(
        spanResult?.paintLayerUnitsB ??
          spanResult?.paint_layer_units_b ??
          spanResult?.paintLayerCountB ??
          spanResult?.paint_layer_count_b,
      ),
    },
    {
      repairArea: toNum(spanResult?.areaC),
      layerUnits: toNum(
        spanResult?.paintLayerUnitsC ??
          spanResult?.paint_layer_units_c ??
          spanResult?.paintLayerCountC ??
          spanResult?.paint_layer_count_c,
      ),
    },
  ];

  let totalRepairArea = 0;
  let accumulatedLayerArea = 0;

  bucketDefinitions.forEach((bucket) => {
    if (!(bucket.repairArea > 0) || !(bucket.layerUnits > 0)) {
      return;
    }

    totalRepairArea += bucket.repairArea;
    accumulatedLayerArea += bucket.repairArea * bucket.layerUnits;
  });

  if (!(totalRepairArea > 0) || !(accumulatedLayerArea > 0)) {
    return {
      effectiveLayers: 0,
      calendarDaysRequired: 0,
      isConstraining: false,
    };
  }

  const effectiveLayers = accumulatedLayerArea / totalRepairArea;
  const isConstraining = effectiveLayers > normalizedLayersPerDay;

  return {
    effectiveLayers,
    calendarDaysRequired: isConstraining
      ? effectiveLayers / normalizedLayersPerDay
      : 0,
    isConstraining,
  };
};

export const calculateCalendarConstraintWeeks = (
  spanResults,
  layersPerDay,
) => {
  const normalizedLayersPerDay = normalizePositiveNumber(
    layersPerDay,
    DEFAULT_LAYERS_PER_DAY,
  );

  let maxElementEffectiveLayers = 0;
  let maxElementCalendarDays = 0;
  let constrainedElementCount = 0;

  (Array.isArray(spanResults) ? spanResults : []).forEach((spanResult) => {
    const constraint = calculateSpanLayerConstraint(
      spanResult,
      normalizedLayersPerDay,
    );

    if (!(constraint.effectiveLayers > 0)) {
      return;
    }

    maxElementEffectiveLayers = Math.max(
      maxElementEffectiveLayers,
      constraint.effectiveLayers,
    );

    if (constraint.isConstraining) {
      constrainedElementCount += 1;
      maxElementCalendarDays = Math.max(
        maxElementCalendarDays,
        constraint.calendarDaysRequired,
      );
    }
  });

  return {
    layersPerDay: normalizedLayersPerDay,
    maxElementEffectiveLayers,
    maxElementCalendarDays,
    constrainedElementCount,
    weeksLowerBound:
      maxElementCalendarDays > 0
        ? maxElementCalendarDays / WORK_DAYS_PER_WEEK
        : 0,
  };
};

export const calculateGeneralCostBaseTotal = ({
  data,
  weeksRequired,
  weekCountForPreview,
  teamsRequired,
}) => {
  const calculationType =
    data?.calculation_type || data?.calculationType || "qty_rate";
  const quantity = toNum(data?.quantity);
  const quantityScalesWithTeams = Boolean(
    data?.quantity_scales_with_teams || data?.quantityScalesWithTeams,
  );
  const rate = toNum(data?.rate || data?.cost_per_unit || data?.costPerUnit);
  const ratePerWeek = toNum(data?.rate_per_week || data?.ratePerWeek);
  const hoursPerDay = toNum(data?.hours_per_day || data?.hoursPerDay);
  const dayCount = toNum(data?.day_count || data?.dayCount);
  const weekCount =
    weekCountForPreview !== undefined
      ? toNum(weekCountForPreview)
      : toNum(data?.week_count || data?.weekCount);
  const movementFrequencyDays = toNum(
    data?.movement_frequency_days || data?.movementFrequencyDays,
  );
  const effectiveWeeks =
    weeksRequired !== undefined ? toNum(weeksRequired) : weekCount;
  const effectiveQuantity =
    quantityScalesWithTeams && teamsRequired !== undefined
      ? quantity * Math.max(0, toNum(teamsRequired))
      : quantity;

  if (calculationType === "qty_rate_per_week_weeks") {
    return effectiveQuantity * ratePerWeek * effectiveWeeks;
  }

  if (calculationType === "qty_rate_hours_day_days") {
    return effectiveQuantity * rate * hoursPerDay * dayCount;
  }

  if (calculationType === "qty_rate_movement_frequency_days") {
    if (!(movementFrequencyDays > 0)) return 0;
    const movements = Math.max(
      0,
      Math.ceil((effectiveWeeks * 7) / movementFrequencyDays),
    );
    return effectiveQuantity * rate * movements;
  }

  return effectiveQuantity * rate;
};

export const buildGeneralCostFormulaPreviewText = (data) => {
  const calculationType =
    data?.calculation_type || data?.calculationType || "qty_rate";
  const quantity = toNum(data?.quantity);
  const quantityScalesWithTeams = Boolean(
    data?.quantity_scales_with_teams || data?.quantityScalesWithTeams,
  );
  const quantityText = quantityScalesWithTeams
    ? `${quantity} x #Teams`
    : `${quantity}`;
  const rate = toNum(data?.rate || data?.cost_per_unit || data?.costPerUnit);
  const ratePerWeek = toNum(data?.rate_per_week || data?.ratePerWeek);
  const hoursPerDay = toNum(data?.hours_per_day || data?.hoursPerDay);
  const dayCount = toNum(data?.day_count || data?.dayCount);
  const weekCount = toNum(data?.week_count || data?.weekCount);
  const movementFrequencyDays = toNum(
    data?.movement_frequency_days || data?.movementFrequencyDays,
  );

  if (calculationType === "qty_rate_per_week_weeks") {
    return `${quantityText} x ${ratePerWeek} x ${weekCount}`;
  }

  if (calculationType === "qty_rate_hours_day_days") {
    return `${quantityText} x ${rate} x ${hoursPerDay} x ${dayCount}`;
  }

  if (calculationType === "qty_rate_movement_frequency_days") {
    return `${quantityText} x ${rate} x ceil((# Weeks x 7) / ${movementFrequencyDays || 0})`;
  }

  return `${quantityText} x ${rate}`;
};
